import { FormEvent, useEffect, useState } from "react";
import { useLoginWithCredentialsMutation } from "../app/services/api";
import UsernameBox from "../components/UsernameBox/UsernameBox";
import TokenBox from "../components/TokenBox/TokenBox";
import PasswordBox from "../components/PasswordBox/PasswordBox";

export type passwordError = {
    message: string,
    code: string,
    detail: {
        remaining_attempts?: number,
        remaining_lockout_seconds?: number
    }
}

const defaultError = {
    message: '',
    code: '',
    detail: {}
};

const Login = () => {
    const [token, setToken] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState(defaultError);
    const [incorrectOTP, setIncorrectOTP] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentForm, setCurrentForm] = useState("username");
    const [login] = useLoginWithCredentialsMutation();

    useEffect(() => {
        /* Disable error as user tries to enter the correct password/token */

        setIncorrectOTP(false);
        setPasswordError(defaultError);
    }, [password, token]);

    const handleLoginQuery = async (ev: FormEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = ev.target as HTMLFormElement;

        if (!target?.checkValidity()) {
            return;
        }
        setLoading(true);
        try {
            const loginResult = await login({ username, password, token });

            if ("error" in loginResult && "status" in loginResult.error) {
                if (loginResult.error.status === 499) {
                    setCurrentForm("token");
                } else {
                    const errorCode = loginResult.error.status as number;
                    const errorResponse = loginResult.error.data;


                    switch (errorCode) {
                        case 400:
                            setPasswordError(errorResponse as passwordError);
                            break;

                        case 403:
                            if ((errorResponse as passwordError)?.code === 'account-locked') {
                                setPasswordError(errorResponse as passwordError);
                            } else {
                                setIncorrectOTP(true);
                            }

                            break;

                        default:
                            break;
                    }
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const renderPasswordForm = () => (
        <PasswordBox
            value={password}
            username={username}
            setPassword={(value: string) => setPassword(value)}
            onCancel={() => cancel()}
            error={passwordError}
            loading={loading}
            handleLogin={handleLoginQuery}
        />
    );

    const cancel = () => {
        setCurrentForm("username");
        setPassword("");
        setToken("");
        setUsername("");
        setIncorrectOTP(false);
        setPasswordError(defaultError);
    };

    const renderTokenForm = () => (
        <TokenBox
            setToken={setToken}
            value={token}
            onCancel={cancel}
            handleSubmit={handleLoginQuery}
            error={incorrectOTP}
            loading={loading}
        />
    );

    const handleUsernameSubmit = (ev: FormEvent) => {
        // Prevent the native form submission: it would navigate to the current
        // URL with the form fields as the query string, dropping the existing
        // query params (application, provider, path, …). Submitting via the form
        // (rather than a button onClick) is what lets 1Password fill & submit.
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentForm('password');
    };

    const renderUsernameForm = () => (
        <UsernameBox value={username} setUsername={setUsername} onClick={handleUsernameSubmit} loading={loading} />
    );

    const content = () => {
        switch (currentForm) {
            case "password":
                return renderPasswordForm();

            case "token":
                return renderTokenForm();

            case "username":
            default:
                return renderUsernameForm();
        }
    };

    return content();
};

export default Login;
